/**
 * Apple TV → Playlists. A direct port of the corporate dashboard's
 * appletv-playlists-manager: the same list with inline icon actions, the same
 * drag-to-reorder editor with per-item dwell time and date range, the same
 * media picker grid. Only the styling is Prospera's.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Eye,
  Film,
  GripVertical,
  ImageOff,
  ListVideo,
  Loader2,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import {
  addPlaylistItem,
  createPlaylist,
  deletePlaylist,
  getPlaylist,
  listMedia,
  listPlaylists,
  removePlaylistItem,
  reorderPlaylistItems,
  updatePlaylist,
  updatePlaylistItem,
  type AppleTvMedia,
  type AppleTvPlaylist,
  type AppleTvPlaylistItem,
} from '@/services/appleTv.service';
import { AppleTvGate, AppleTvPageShell, shouldGate, useAppleTvAccess } from './useAppleTvAccess';
import { IconAction, Table } from './AppleTvTable';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  formatSchedule,
  isIsoDate,
  scheduleRangeError,
  scheduleStatus,
  toApiDate,
  type ScheduleStatus,
} from './playlistSchedule';
import {
  isNoLongerAvailable,
  SHARED_PLAYLIST_BADGE,
  sharedPlaylistFailure,
  signageErrorMessage,
  type SharedPlaylist,
} from './sharedPlaylists';
import { SharedPlaylistViewer } from './SharedPlaylistViewer';

export default function AppleTvPlaylistsPage() {
  const access = useAppleTvAccess();
  const { configured, canManage } = access;

  const [playlists, setPlaylists] = useState<AppleTvPlaylist[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AppleTvPlaylist | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AppleTvPlaylist | null>(null);

  const [editorId, setEditorId] = useState<number | null>(null);
  const [viewing, setViewing] = useState<{ playlist: AppleTvPlaylist; entry: SharedPlaylist } | null>(null);

  /** A playlist another user shared with this account opens read-only: only its owner can change it. */
  const openPlaylist = (p: AppleTvPlaylist) => {
    if (!p.sharedWithMe) {
      setEditorId(p.id);
      return;
    }
    setViewing({ playlist: p, entry: { status: 'loading' } });
    getPlaylist(p.id)
      .then((data) =>
        setViewing((v) => (v?.playlist.id === p.id ? { ...v, entry: { status: 'ok', data } } : v)),
      )
      .catch((error) =>
        setViewing((v) =>
          v?.playlist.id === p.id ? { ...v, entry: sharedPlaylistFailure(error, () => openPlaylist(p)) } : v,
        ),
      );
  };

  useEffect(() => {
    if (!configured) return;
    setLoading(true);
    listPlaylists()
      .then(setPlaylists)
      .catch(() => toast.error('Failed to load playlists'))
      .finally(() => setLoading(false));
  }, [configured]);

  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () => (q ? playlists.filter((p) => p.name.toLowerCase().includes(q)) : playlists),
    [playlists, q],
  );

  const openCreate = () => {
    setEditing(null);
    setName('');
    setDialogOpen(true);
  };

  const openRename = (p: AppleTvPlaylist) => {
    setEditing(p);
    setName(p.name);
    setDialogOpen(true);
  };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        const updated = await updatePlaylist(editing.id, { name: name.trim() });
        setPlaylists((prev) => prev.map((p) => (p.id === updated.id ? { ...p, ...updated } : p)));
        toast.success('Playlist renamed');
      } else {
        const created = await createPlaylist(name.trim());
        setPlaylists((prev) => [...prev, created]);
        toast.success('Playlist created');
      }
      setDialogOpen(false);
    } catch {
      toast.error('Failed to save playlist');
    } finally {
      setSaving(false);
    }
  };

  const setDefault = async (p: AppleTvPlaylist) => {
    try {
      await updatePlaylist(p.id, { isDefault: true });
      // The default is per owner, so playlists shared with this account keep theirs.
      setPlaylists((prev) => prev.map((x) => (x.sharedWithMe ? x : { ...x, isDefault: x.id === p.id })));
      toast.success(`“${p.name}” is now the default playlist`);
    } catch {
      toast.error('Failed to set default');
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deletePlaylist(deleteTarget.id);
      setPlaylists((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success('Playlist deleted');
    } catch (error) {
      // A 409 names other people's screens or groups an administrator assigned
      // it to; the API's message says which, so it is shown as-is.
      toast.error(signageErrorMessage(error, 'Failed to delete playlist'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const gated = shouldGate(access);

  return (
    <AppleTvPageShell>
      <div className="shrink-0 p-6 pb-0">
        <PageHeader title="Playlists" subtitle="What plays on your screens, and in what order." />
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-6">
        {gated ? (
          <AppleTvGate {...access} />
        ) : (
          <Table.Root>
            <Table.Toolbar>
              <div className="relative min-w-[200px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search playlists..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {canManage && (
                <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>
                  Add Playlist
                </Button>
              )}
            </Table.Toolbar>

            {loading ? (
              <Table.Loading />
            ) : (
              <Table.Content>
                <table className="w-full table-fixed">
                  <Table.Header>
                    <tr>
                      <Table.Th className="w-[45%]">Name</Table.Th>
                      <Table.Th className="w-32">Default</Table.Th>
                      <Table.Th className="w-48 text-right">Actions</Table.Th>
                    </tr>
                  </Table.Header>
                  <tbody className="divide-y divide-slate-200">
                    {filtered.length === 0 ? (
                      <Table.Empty colSpan={3} message="No playlists yet." />
                    ) : (
                      filtered.map((p) => (
                        <Table.Row key={p.id} onClick={() => openPlaylist(p)}>
                          <Table.Cell>
                            <div className="flex min-w-0 items-center gap-2 font-medium text-slate-900">
                              <ListVideo className="h-4 w-4 shrink-0 text-slate-400" />
                              <span className="truncate">{p.name}</span>
                              {p.sharedWithMe && (
                                <Badge variant="purple" size="sm">
                                  {SHARED_PLAYLIST_BADGE}
                                </Badge>
                              )}
                            </div>
                          </Table.Cell>
                          <Table.Cell>
                            {p.isDefault && !p.sharedWithMe && (
                              <Badge variant="info">
                                <Star className="mr-1 h-3 w-3" /> Default
                              </Badge>
                            )}
                          </Table.Cell>
                          <Table.Cell actions>
                            {p.sharedWithMe ? (
                              <IconAction title="View items" tone="primary" onClick={() => openPlaylist(p)}>
                                <Eye className="h-4 w-4" />
                              </IconAction>
                            ) : (
                              <IconAction title="Edit items" tone="primary" onClick={() => setEditorId(p.id)}>
                                <ListVideo className="h-4 w-4" />
                              </IconAction>
                            )}
                            {canManage && !p.sharedWithMe && !p.isDefault && (
                              <IconAction title="Set as default" tone="primary" onClick={() => setDefault(p)}>
                                <Star className="h-4 w-4" />
                              </IconAction>
                            )}
                            {canManage && !p.sharedWithMe && (
                              <>
                                <IconAction title="Rename" tone="primary" onClick={() => openRename(p)}>
                                  <Pencil className="h-4 w-4" />
                                </IconAction>
                                <IconAction title="Delete" tone="danger" onClick={() => setDeleteTarget(p)}>
                                  <Trash2 className="h-4 w-4" />
                                </IconAction>
                              </>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))
                    )}
                  </tbody>
                </table>
              </Table.Content>
            )}
          </Table.Root>
        )}
      </div>

      {/* Create / rename dialog */}
      <Modal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? 'Rename Playlist' : 'Add Playlist'}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving} disabled={!name.trim()}>
              {editing ? 'Update' : 'Create'}
            </Button>
          </div>
        }
      >
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter playlist name"
            autoFocus
          />
        </FormField>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={doDelete}
        title={`Delete “${deleteTarget?.name}”?`}
        message="All its items are removed and any screen or group using it falls back. The media files themselves stay in the library."
        confirmLabel="Delete"
      />

      {editorId != null && (
        <PlaylistEditor playlistId={editorId} canManage={canManage} onClose={() => setEditorId(null)} />
      )}

      {viewing && (
        <SharedPlaylistViewer
          playlistId={viewing.playlist.id}
          entry={viewing.entry}
          onPlaylistsPage
          onClose={() => setViewing(null)}
        />
      )}
    </AppleTvPageShell>
  );
}

// ── Playlist editor dialog ────────────────────────────────────────────────

function PlaylistEditor({
  playlistId,
  canManage,
  onClose,
}: {
  playlistId: number;
  canManage: boolean;
  onClose: () => void;
}) {
  const [playlistName, setPlaylistName] = useState('');
  const [items, setItems] = useState<AppleTvPlaylistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [media, setMedia] = useState<AppleTvMedia[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [busyItem, setBusyItem] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getPlaylist(playlistId), listMedia()])
      .then(([data, mediaList]) => {
        if (cancelled) return;
        setPlaylistName(data.playlist.name);
        setItems([...data.items].sort((a, b) => a.sortOrder - b.sortOrder));
        setMedia(mediaList);
      })
      .catch(() => toast.error('Failed to load playlist'))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [playlistId]);

  const persistOrder = async (ordered: AppleTvPlaylistItem[]) => {
    try {
      await reorderPlaylistItems(
        playlistId,
        ordered.map((it, i) => ({ id: it.id, sortOrder: i + 1 })),
      );
    } catch {
      toast.error('Could not save order');
    }
  };

  const onDrop = (index: number) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    const renumbered = next.map((it, i) => ({ ...it, sortOrder: i + 1 }));
    setItems(renumbered);
    setDragIndex(null);
    void persistOrder(renumbered);
  };

  /** Saves a patch; false when it was refused, with the API's own message shown. */
  const commitItem = async (
    item: AppleTvPlaylistItem,
    patch: Partial<Pick<AppleTvPlaylistItem, 'duration' | 'startDate' | 'endDate'>>,
  ): Promise<boolean> => {
    setBusyItem(item.id);
    try {
      const updated = await updatePlaylistItem(playlistId, item.id, patch);
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, ...updated, media: it.media } : it)));
      return true;
    } catch (error) {
      toast.error(signageErrorMessage(error, 'Failed to update item'));
      return false;
    } finally {
      setBusyItem(null);
    }
  };

  const removeItem = async (item: AppleTvPlaylistItem) => {
    setBusyItem(item.id);
    try {
      await removePlaylistItem(playlistId, item.id);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
    } catch {
      toast.error('Failed to remove item');
    } finally {
      setBusyItem(null);
    }
  };

  const addMedia = async (m: AppleTvMedia) => {
    try {
      const created = await addPlaylistItem(playlistId, { mediaId: m.id });
      setItems((prev) => [
        ...prev,
        { ...created, media: { id: m.id, name: m.name, type: m.type, url: m.url } },
      ]);
      setPickerOpen(false);
    } catch (error) {
      if (isNoLongerAvailable(error)) {
        // Not this account's any more, or deleted: refresh the picker.
        toast.error('That media is no longer available.');
        listMedia()
          .then(setMedia)
          .catch(() => undefined);
        return;
      }
      toast.error(signageErrorMessage(error, 'Failed to add media'));
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={playlistName || 'Playlist'}
        size="xl"
        // Fixed height, as on the dashboard: the list scrolls inside a panel
        // that stays the same size whether it holds two items or forty.
        // `flex-none` is load-bearing: the body is `flex-1` (flex-basis 0%),
        // and in a column flex box flex-basis beats `height`, so without it
        // the 70vh is silently ignored and the panel sizes to its contents.
        bodyClassName="md:h-[70vh] md:flex-none"
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <div>
              {canManage && !loading && items.length > 0 && (
                <Button variant="secondary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setPickerOpen(true)}>
                  Add media
                </Button>
              )}
            </div>
            <Button onClick={onClose}>Done</Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-slate-500">
          Drag to reorder. Set how long each item shows and an optional schedule: it shows from the
          start of the start date through the end of the end date, in each screen&apos;s local time.
          Leave a date blank for no limit.
        </p>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Film className="h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">This playlist is empty.</p>
            {canManage && (
              <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setPickerOpen(true)}>
                Add media
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-2 py-2">
            {items.map((item, index) => (
              <div
                key={item.id}
                draggable={canManage}
                onDragStart={() => setDragIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(index)}
                onDragEnd={() => setDragIndex(null)}
                className={`flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2 ${
                  dragIndex === index ? 'opacity-50' : ''
                }`}
              >
                <GripVertical className={`h-4 w-4 shrink-0 text-slate-400 ${canManage ? 'cursor-grab' : ''}`} />
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-slate-100">
                  {item.media?.type === 'video' ? (
                    <Film className="h-5 w-5 text-slate-400" />
                  ) : item.media?.url ? (
                    <img src={item.media.url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageOff className="h-5 w-5 text-slate-300" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">
                    {item.media?.name ?? `Media #${item.mediaId}`}
                  </p>
                  <p className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="capitalize">{item.media?.type ?? 'media'}</span>
                    <span aria-hidden="true">·</span>
                    <span className="truncate">{formatSchedule(item.startDate, item.endDate)}</span>
                    <ScheduleBadge status={scheduleStatus(item.startDate, item.endDate)} />
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="text-xs text-slate-500">Secs</span>
                  <Input
                    type="number"
                    min={1}
                    max={3600}
                    defaultValue={item.duration}
                    className="h-8 w-20"
                    disabled={!canManage}
                    onBlur={(e) => {
                      const v = Math.max(1, Math.min(3600, Number(e.target.value) || 10));
                      if (v !== item.duration) commitItem(item, { duration: v });
                    }}
                  />
                </div>
                <ItemScheduleFields
                  item={item}
                  disabled={!canManage}
                  onCommit={(dates) => commitItem(item, dates)}
                />
                {canManage && (
                  <IconAction
                    title="Remove from playlist"
                    tone="danger"
                    disabled={busyItem === item.id}
                    onClick={() => removeItem(item)}
                  >
                    {busyItem === item.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </IconAction>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Media picker */}
      <Modal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Add media"
        size="lg"
        bodyClassName="md:h-[60vh] md:flex-none"
        footer={
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setPickerOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        <p className="mb-3 text-sm text-slate-500">Pick from the media library.</p>
        {media.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-500">
            No media in the library yet. Upload some on the Media page.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3 py-2 sm:grid-cols-4">
            {media.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => addMedia(m)}
                className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:ring-2 hover:ring-primary"
                title={`Add ${m.name}`}
              >
                {m.type === 'video' ? (
                  <video src={m.url} className="h-full w-full object-cover" muted />
                ) : (
                  <img src={m.url} alt={m.name} className="h-full w-full object-cover" />
                )}
                {m.type === 'video' && (
                  <span className="absolute left-1 top-1 flex items-center gap-0.5 rounded bg-black/60 px-1 py-0.5 text-[10px] text-white">
                    <Film className="h-2.5 w-2.5" /> video
                  </span>
                )}
                <span className="absolute inset-x-0 bottom-0 truncate bg-black/50 px-1.5 py-1 text-left text-[11px] text-white">
                  {m.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
}

/**
 * One item's Start/End dates. Both are optional whole days, sent as the
 * YYYY-MM-DD the picker holds (it builds them from local calendar parts, never
 * toISOString()) or null when cleared.
 *
 * Edits are a local draft: a range the API would refuse is shown inline and
 * never sent, and a valid one is saved with BOTH dates in one request, so the
 * API never checks a new date against a stale stored one. A refused save goes
 * back to the stored dates.
 */
function ItemScheduleFields({
  item,
  disabled,
  onCommit,
}: {
  item: AppleTvPlaylistItem;
  disabled: boolean;
  onCommit: (dates: { startDate: string | null; endDate: string | null }) => Promise<boolean>;
}) {
  const [start, setStart] = useState(item.startDate ?? '');
  const [end, setEnd] = useState(item.endDate ?? '');

  // A save (or a refused one) changes the stored dates: show them.
  useEffect(() => {
    setStart(item.startDate ?? '');
    setEnd(item.endDate ?? '');
  }, [item.startDate, item.endDate]);

  const error = scheduleRangeError(toApiDate(start), toApiDate(end));

  const change = async (nextStart: string, nextEnd: string) => {
    setStart(nextStart);
    setEnd(nextEnd);
    const startDate = toApiDate(nextStart);
    const endDate = toApiDate(nextEnd);
    if (scheduleRangeError(startDate, endDate)) return;
    if (startDate === (item.startDate ?? null) && endDate === (item.endDate ?? null)) return;
    if (!(await onCommit({ startDate, endDate }))) {
      setStart(item.startDate ?? '');
      setEnd(item.endDate ?? '');
    }
  };

  return (
    <div className="flex shrink-0 flex-col items-end gap-0.5">
      <div className="flex items-center gap-1.5">
        <DatePicker
          size="sm"
          value={start}
          max={isIsoDate(end) ? end : undefined}
          wrapperClassName="w-36"
          ariaLabel="Start date"
          placeholder="Start date"
          disabled={disabled}
          clearable
          onChange={(value) => void change(value, end)}
        />
        <span className="text-xs text-slate-400">→</span>
        <DatePicker
          size="sm"
          value={end}
          min={isIsoDate(start) ? start : undefined}
          wrapperClassName="w-36"
          ariaLabel="End date"
          placeholder="End date"
          disabled={disabled}
          clearable
          onChange={(value) => void change(start, value)}
        />
      </div>
      {error && (
        <p role="alert" className="text-[11px] text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

const SCHEDULE_BADGE: Record<
  Exclude<ScheduleStatus, 'always'>,
  { label: string; variant: 'info' | 'success' | 'neutral' }
> = {
  scheduled: { label: 'Scheduled', variant: 'info' },
  active: { label: 'Active', variant: 'success' },
  expired: { label: 'Expired', variant: 'neutral' },
};

/** Today's standing by the browser's local date; nothing for an item with no dates. */
function ScheduleBadge({ status }: { status: ScheduleStatus }) {
  if (status === 'always') return null;
  const { label, variant } = SCHEDULE_BADGE[status];
  return (
    <Badge variant={variant} size="sm">
      {label}
    </Badge>
  );
}
